import React, { useEffect } from 'react';
import { NextPageWithLayout } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import extractFromContext from '@helpers/utils/extract-from-context';
import Head from '@components/atoms/head/head';
import { usePreferences } from '@context/preferences.context';
import { defaultMetas } from '@constants/metas/default';
import { getAppInitialProps } from '@helpers/initial-props';
import NegotiationListingsLayout from '@layouts/negotiation-listings-layout/negotiation-listings-layout';
import PageAuthenticationGuard from '@components/atoms/authentication/page-authentication-guard/page-authentication-guard';
import SellingOffers from '@components/organisms/selling-offers/selling-offers';
import { ListItemSelectContextProvider } from '@context/negotiation-listings-split-view.context';
import { SellingOffersProvider } from '@context/selling-offers.context';
import { NegotiationProvider } from '@context/negotiation.context';
import { useAnalytics } from '@context/analytics/analytics.context';
import { SELLING_OFFERS_PATH } from '@constants/negotiation';

const SellingOffersPage: NextPageWithLayout = () => {
  const { language } = usePreferences();
  const pageMetas = defaultMetas[language];
  const { t } = useTranslation('negotiation');
  const { updateDataLayer } = useAnalytics();

  const breadcrumbs = [
    {
      localizedName: t('NEGOTIATION_LISTING.NAVIGATION.HOME'),
      url: {
        path: '/',
      },
    },
    {
      localizedName: t('NEGOTIATION_LISTING.NAVIGATION.SELLING_OFFERS'),
      url: {
        path: SELLING_OFFERS_PATH,
      },
    },
  ];

  useEffect(() => {
    updateDataLayer({
      screen_name: '/selling_offers',
      screen_category: 'selling_offers',
      screen_subcategory: 'selling_offers',
    });
  }, []);

  return (
    <PageAuthenticationGuard redirectionUrl={'/'}>
      <NegotiationListingsLayout
        breadcrumbs={breadcrumbs}
        title={t('NEGOTIATION_LISTING.SELLING_OFFERS.TITLE')}
        showSellingOffersBanner={true}
      >
        <Head metadata={{ ...pageMetas }} />
        <SellingOffersProvider>
          <ListItemSelectContextProvider>
            <NegotiationProvider>
              <SellingOffers />
            </NegotiationProvider>
          </ListItemSelectContextProvider>
        </SellingOffersProvider>
      </NegotiationListingsLayout>
    </PageAuthenticationGuard>
  );
};

export async function getServerSideProps(context) {
  const { locale } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, [
    'default-layout',
    'common',
    'negotiation',
    'orders-sales',
    'vc-authentication',
    'a11y',
  ]);

  return {
    props: {
      ...initialProps,
      ...translations,
    },
  };
}

SellingOffersPage.Layout = [];
SellingOffersPage.PageType = 'SellingOffersPage';

export default SellingOffersPage;
